import React, { useMemo } from "react";
import { ParallaxProvider, Parallax } from "react-scroll-parallax";
import { useSpring, animated } from "react-spring";
import { Line } from "react-chartjs-2";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMountain,
  faUtensils,
  faPalette,
  faLandmark,
  faSpa,
  faMusic,
  faHandsHelping,
  faCamera
} from "@fortawesome/free-solid-svg-icons";
import "react-accessible-accordion/dist/fancy-example.css";
import "chart.js/auto";
import "./About.css";

const ExpansionChart = () => {
  const chartData = useMemo(
    () => ({
      labels: ["2025", "2026", "2027", "2028", "2029", "2030"],
      datasets: [
        {
          label: "New Countries Reached",
          data: [1, 3, 7, 12, 18, 25],
          borderColor: "#2DD4BF",
          backgroundColor: "rgba(45, 212, 191, 0.2)",
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: "#2DD4BF",
          pointHoverRadius: 6
        },
        {
          label: "User Growth (in thousands)",
          data: [10, 50, 150, 400, 800, 1500],
          borderColor: "#F59E0B",
          backgroundColor: "rgba(245, 158, 11, 0.2)",
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: "#F59E0B",
          pointHoverRadius: 6
        },
      ],
    }),
    []
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            font: { size: 14, family: "'Inter', sans-serif", weight: 600 },
            color: "#F3F4F6",
            usePointStyle: true,
            padding: 20
          },
        },
        tooltip: {
          backgroundColor: "#1F2937",
          titleFont: { size: 16, weight: 600 },
          bodyFont: { size: 14 },
          padding: 12,
          usePointStyle: true,
          cornerRadius: 8
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { 
            color: "#9CA3AF", 
            font: { size: 12, family: "'Inter', sans-serif" } 
          },
        },
        y: {
          grid: { color: "rgba(156, 163, 175, 0.1)" },
          ticks: { 
            color: "#9CA3AF", 
            font: { size: 12, family: "'Inter', sans-serif" } 
          },
        },
      },
    }),
    []
  );

  return (
    <div className="chart-container">
      <h2 className="text-center text-3xl font-bold text-teal-100 mb-8">
        Global Expansion Journey
        <span className="block text-teal-400 text-xl font-normal mt-2">2025-2030 Roadmap</span>
      </h2>
      <div className="chart-wrapper bg-white/10 p-6 rounded-2xl backdrop-blur-sm border border-teal-400/20">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

const FAQ = () => {
  const fadeIn = useSpring({
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
    config: { duration: 1000 },
  });

  const gradientBg = {
    background: "linear-gradient(135deg, rgba(19,78,94,0.95) 0%, rgba(15,64,87,0.95) 100%)",
  };

  const cardHover = useSpring({
    from: { transform: "scale(1)", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" },
    to: { transform: "scale(1.02)", boxShadow: "0 10px 15px rgba(0, 0, 0, 0.2)" },
    config: { tension: 200, friction: 15 },
  });

  return (
    <ParallaxProvider>
      <div className="faq-page" style={{ 
        backgroundImage: "linear-gradient(rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.95)), url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
        backgroundPosition: "center",
        minHeight: "100vh"
      }}>
        <Parallax speed={-10}>
          <header className="faq-header relative overflow-hidden" style={gradientBg}>
            <div className="header-content text-center py-24">
              <h3 className="text-white text-5xl font-bold mb-4 tracking-tight">
                <span className="bg-gradient-to-r from-teal-400 to-cyan-500 bg-clip-text text-transparent">
                  Enceptics Adventures
                </span>
              </h3>
              <p className="text-xl text-teal-200 font-light max-w-2xl mx-auto leading-relaxed">
                Transformative Journeys That Leave Permanent Memories
              </p>
              <div className="absolute inset-0 opacity-20 mix-blend-soft-light">
                <svg viewBox="0 0 100 100" className="absolute top-0 left-0 w-full h-full">
                  <path d="M0 50 Q 25 25 50 50 Q 75 75 100 50" stroke="white" strokeWidth="2" fill="none" />
                  <path d="M0 30 Q 40 50 100 70" stroke="white" strokeWidth="1" fill="none" />
                </svg>
              </div>
            </div>
          </header>
        </Parallax>

        {/* Rest of the component remains similar but with updated styling */}
        {/* Update accordion sections with travel icons */}
        <AccordionItemHeading>
          <AccordionItemButton className="accordion-button bg-teal-900/40 hover:bg-teal-800/40 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <FontAwesomeIcon icon={faMountain} className="w-5 h-5 text-teal-400" />
              <span>Adventure Enthusiasts</span>
            </div>
            <svg className="accordion-arrow w-5 h-5 transform transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </AccordionItemButton>
        </AccordionItemHeading>

        {/* Update other accordion items similarly with appropriate icons */}
        
        {/* Updated footer section */}
        <Parallax speed={5}>
          <animated.section 
            style={cardHover}
            className="faq-footer max-w-4xl mx-auto my-12 p-8 rounded-xl bg-gradient-to-r from-teal-500/30 to-cyan-500/30 border border-teal-400/30 shadow-2xl backdrop-blur-sm"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Crafting <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Unforgettable Experiences</span>
            </h2>
            <p className="text-teal-100 text-lg leading-relaxed">
              Partnering with local experts and sustainable tourism organizations to create authentic,
              meaningful connections between travelers and destinations.
            </p>
          </animated.section>
        </Parallax>
      </div>
    </ParallaxProvider>
  );
};

export default FAQ;